@let footer = footerStore.footer;
@let hasChanges = hasUnsavedChanges(footerForm);
@if (footerStore.isLoading()) {
  <div class="pt-32">
    <aw-loading-animation></aw-loading-animation>
  </div>
}
@if (footer() && !footerStore.isLoading()) {
  <aw-section [formGroup]="footerForm" testIdAffix="footer">
    <aw-section-header>
      <aw-section-headline>
        {{ cmsTextProvider.shared.footer }}
      </aw-section-headline>
      <aw-section-header-actions>
        <aw-reveal-form-errors-button [form]="footerForm" />
        <aw-button
          [id]="'discard-changes'"
          [style]="'primary-alt'"
          [type]="'reset'"
          [isDisabled]="!hasChanges"
          (click)="handleAbortChangesDialog()"
          [text]="cmsTextProvider.shared.cancel">
        </aw-button>
        <aw-button
          [id]="'save-entity'"
          [style]="'primary'"
          (click)="save()"
          [type]="'submit'"
          [isDisabled]="!footerForm.valid || !hasChanges"
          [text]="cmsTextProvider.shared.save">
        </aw-button>
      </aw-section-header-actions>
    </aw-section-header>
    <!-- Aussehen -->
    <aw-section testIdAffix="Appearance" [formGroup]="footerForm">
      <aw-section-header>
        <aw-section-headline>
          {{ cmsTextProvider.shared.appearance }}
        </aw-section-headline>
      </aw-section-header>
      <!-- Colors -->
      <aw-section testIdAffix="Colors">
        <div class="flex w-full gap-2">
          <div
            class="border-standard-outline-light flex w-full flex-col gap-2 rounded-md border p-4">
            <div class="flex justify-end">
              <div
                class="bg-standard-surface text-md flex w-fit items-center gap-2 rounded-full px-2 py-1">
                <span class="material-symbols-rounded cursor-pointer"
                  >light_mode</span
                >
                <span>{{ cmsTextProvider.footer.lightMode }}</span>
              </div>
            </div>
            <div class="relative flex items-center gap-2">
              <aw-color-picker-list
                [buttonSize]="'lg'"
                [color]="footerForm.get('appearance')?.get('fontColor')?.value ?? ''"
                (colorChanged)="onColorChosen($event, 'fontColor')"></aw-color-picker-list>
              <span>{{ cmsTextProvider.footer.font }}</span>
            </div>
            <div class="relative flex items-center gap-2">
              <aw-color-picker-list
                [buttonSize]="'lg'"
                [color]="footerForm.get('appearance')?.get('backgroundColor')?.value ?? ''"
                (colorChanged)="onColorChosen($event, 'backgroundColor')"></aw-color-picker-list>
              <span>{{ cmsTextProvider.footer.background }}</span>
            </div>
          </div>
          <div
            class="border-standard-outline-light bg-primary-dark text-primary-on flex w-full flex-col gap-2 rounded-md border p-4">
            <div class="flex justify-end">
              <div
                class="bg-standard-surface text-md text-standard-text-primary flex w-fit items-center gap-2 rounded-full px-2 py-1">
                <span class="material-symbols-rounded cursor-pointer"
                  >dark_mode</span
                >
                <span>{{ cmsTextProvider.footer.darkMode }}</span>
              </div>
            </div>

            <div class="relative flex items-center gap-2">
              <aw-color-picker-list
                [buttonSize]="'lg'"
                [color]="footerForm.get('appearance')?.get('fontColorDark')?.value ?? ''"
                (colorChanged)="onColorChosen($event, 'fontColorDark')"></aw-color-picker-list>
              <span>{{ cmsTextProvider.footer.font }}</span>
            </div>
            <div class="relative flex items-center gap-2">
              <aw-color-picker-list
                [buttonSize]="'lg'"
                [color]="footerForm.get('appearance')?.get('backgroundColorDark')?.value ?? ''"
                (colorChanged)="onColorChosen($event, 'backgroundColorDark')"></aw-color-picker-list>
              <span>{{ cmsTextProvider.footer.background }}</span>
            </div>
          </div>
        </div>
        <div class="flex items-center gap-2" formGroupName="appearance">
          <aw-checkbox
            formControlName="showBackgroundImage"
            [label]="cmsTextProvider.shared.showBackgroundImage"
            id="showBackgroundImage">
          </aw-checkbox>
        </div>
        @if (
          footerForm.controls.appearance.controls.showBackgroundImage.value
        ) {
          <div class="flex w-full items-end gap-2" formGroupName="appearance">
            <div class="w-full">
              <span
                class="text-standard-text-secondary text-md mb-1 block font-semibold"
                >{{ cmsTextProvider.footer.imageSource }}</span
              >
              <aw-image-select
                class="w-full"
                [id]="'footer-background-image'"
                usageLabel="'footer.backgroundImage'"
                formControlName="backgroundImage" />
            </div>

            <div class="w-full">
              <span
                class="text-standard-text-secondary text-md mb-1 block font-semibold"
                >{{ cmsTextProvider.footer.description }}</span
              >
              <aw-translation-accessibility-text-input
                [entityType]="EntityType.footerLinkArea"
                [attr.scroll-mark]="
                  footerForm.controls.appearance.controls.backgroundImageAlt
                "
                [relationType]="TextRelationType.imageAlt"
                [id]="'backgroundImageAlt'"
                [formControl]="
                  footerForm.controls.appearance.controls.backgroundImageAlt
                "
                [label]="''" />
            </div>
          </div>
        }
      </aw-section>
    </aw-section>
    <!-- Layout -->
    <aw-section testIdAffix="Layout" [formGroup]="footerForm">
      <aw-section-header>
        <aw-section-headline>
          {{ cmsTextProvider.shared.layout }}
        </aw-section-headline>
      </aw-section-header>

      <div class="w-full">
        <aw-drag-drop-grid
          [blocks]="footerForm.controls.layout.controls.blockOrder.value"
          [customBlocks]="getCustomLayoutBlocks()"
          [hiddenBlocks]="
            footerForm.controls.layout.controls.hiddenBlocks.value
          "
          (blockOrderChanged)="onBlockOrderChanged($event)"
          (hiddenBlocksChanged)="onHiddenBlocksChanged($event)">
        </aw-drag-drop-grid>
        <span>
          {{ cmsTextProvider.footer.information.prefix }}
          <a [routerLink]="'/cms/corporate-design'"
            ><u>{{ cmsTextProvider.footer.information.linkText }}</u></a
          >
          {{ cmsTextProvider.footer.information.suffix }}
        </span>
        <div class="flex w-full items-center justify-end gap-7">
          <aw-color-picker-input
            formControlName="backgroundColor"
            [label]="cmsTextProvider.shared.backgroundColour"
            id="backgroundColor" />
          <aw-button-group
            id="primaryBorderColorButtonGroup"
            [options]="footerBorderColors | enumToButtonOptionArray"
            [formControl]="backgroundColorBtn"
            (valueChange)="onBackgroundColorChange($event)" />
        </div>
      </div>
    </aw-section>
    <!-- LogoArea -->
    <aw-section
      testIdAffix="logoArea"
      formGroupName="logoArea"
      [isCollapsible]="true"
      [isInitiallyCollapsed]="true">
      <aw-section-header>
        <aw-section-headline>
          {{ cmsTextProvider.footer.block }} 1
          <span class="material-symbols-rounded ml-1 align-middle">
            {{
              getIconForBlockType(
                footerForm.controls.logoArea.controls.type.value
              )
            }}
          </span>
        </aw-section-headline>
        <aw-section-header-actions>
          @if (
            !footerForm.controls.layout.controls.hiddenBlocks.value.includes(
              'logoArea'
            )
          ) {
            <span
              class="bg-info-light text-info-main border-info-light rounded-full border px-2.5 py-1 text-xs font-semibold">
              {{ cmsTextProvider.footer.visible }}
            </span>
          } @else {
            <span
              class="bg-standard-surface border-standard-outline-light text-standard-text-secondary rounded-full border px-2.5 py-1 text-xs font-semibold">
              {{ cmsTextProvider.footer.hidden }}
            </span>
          }
        </aw-section-header-actions>
      </aw-section-header>

      <div class="flex flex-col gap-6 py-4">
        <!-- Type  -->
        <div class="flex items-center gap-6">
          <div class="text-standard-text-secondary w-24 text-sm font-semibold">
            {{ cmsTextProvider.footer.type }}
          </div>
          <div class="w-80">
            <aw-drop-down
              [id]="'footer-type'"
              formControlName="type"
              name="name"
              valueAttribute="value"
              [options]="footerBlockTypeOptions"></aw-drop-down>
          </div>
        </div>

        <!-- Titel -->
        <div class="flex items-center gap-6">
          <div class="text-standard-text-secondary w-24 text-sm font-semibold">
            {{ cmsTextProvider.shared.title }}
          </div>
          <div class="flex items-center gap-6">
            <div class="w-80">
              <aw-translation-text-input
                [entityType]="EntityType.footerLogoArea"
                [attr.scroll-mark]="
                  footerForm.value.logoArea?.headlines?.[0]?.id
                "
                [relationType]="TextRelationType.headline"
                formControlName="headlines"
                [label]="''"
                id="imageTitle">
              </aw-translation-text-input>
            </div>
            <div class="flex items-center gap-2">
              <aw-checkbox
                formControlName="isActive"
                [label]="cmsTextProvider.shared.show"
                id="imageTitle"></aw-checkbox>
            </div>
          </div>
        </div>

        <!-- Logo and Bild-Liste -->
        @if (
          footerForm.controls.logoArea.controls.type.value ===
          FooterBlockType.Logo
        ) {
          <div class="flex items-start gap-6">
            <div
              class="text-standard-text-secondary w-24 pt-3 text-sm font-semibold">
              {{ cmsTextProvider.footer.logo }}
            </div>
            <div class="flex-1">
              @let firstLogo = getFirstLogoControl();
              @if (firstLogo) {
                <div class="grid grid-cols-3 gap-4">
                  <div>
                    <span
                      class="text-standard-text-secondary mb-1 block text-xs font-semibold"
                      >{{ cmsTextProvider.footer.imageSource }}</span
                    >
                    <aw-image-select
                      id="logoImage0"
                      [persistUsageOnSelect]="true"
                      usageLabel="'footer.logoImage'"
                      [formControl]="firstLogo.controls.image"
                      [label]="''" />
                  </div>
                  <div>
                    <span
                      class="text-standard-text-secondary mb-1 block text-xs font-semibold"
                      >{{ cmsTextProvider.footer.description }}</span
                    >
                    <aw-translation-accessibility-text-input
                      [entityType]="EntityType.footerLogoArea"
                      [attr.scroll-mark]="firstLogo.value.imageAlts?.[0]?.id"
                      [relationType]="TextRelationType.imageAlt"
                      id="logoText0"
                      [formControl]="firstLogo.controls.imageAlts"
                      [label]="''" />
                  </div>
                  <div>
                    <span
                      class="text-standard-text-secondary mb-1 block text-xs font-semibold"
                      >{{ cmsTextProvider.footer.linkAddressLabel }}</span
                    >
                    <aw-text-input
                      id="logoAddress0"
                      [formControl]="firstLogo.controls.url"
                      [label]="''" />
                  </div>
                </div>
              }
            </div>
          </div>
        } @else if (
          footerForm.controls.logoArea.controls.type.value ===
          FooterBlockType.Images
        ) {
          <div class="flex items-start gap-6">
            <div
              class="text-standard-text-secondary w-24 pt-3 text-sm font-semibold">
              {{ cmsTextProvider.footer.links }}
            </div>
            <div class="flex flex-1 flex-col gap-4">
              <div class="w-60">
                <aw-button-group
                  id="logo-orientation"
                  [compact]="true"
                  [options]="orientationOptions"
                  [formControl]="
                    footerForm.controls.logoArea.controls.orientation
                  "></aw-button-group>
              </div>
              @for (
                logoAreaForm of footerForm.controls.logoArea.controls
                  .accessibleImages.controls;
                track logoAreaForm;
                let index = $index
              ) {
                <div
                  class="flex items-end gap-4"
                  [attr.data-testid]="'add-image-' + index">
                  <div class="mb-1 flex flex-col">
                    <button
                      type="button"
                      class="disabled:text-standard-text-disabled text-info-main cursor-pointer hover:text-gray-600 disabled:cursor-not-allowed"
                      [disabled]="index === 0"
                      (click)="
                        moveItemAtIndex(
                          footerForm.controls.logoArea.controls
                            .accessibleImages,
                          index,
                          index - 1
                        )
                      ">
                      <span class="material-symbols-rounded"
                        >keyboard_arrow_up</span
                      >
                    </button>
                    <button
                      type="button"
                      class="disabled:text-standard-text-disabled text-info-main cursor-pointer hover:text-gray-600 disabled:cursor-not-allowed"
                      [disabled]="
                        index ===
                        footerForm.controls.logoArea.controls.accessibleImages
                          .length -
                          1
                      "
                      (click)="
                        moveItemAtIndex(
                          footerForm.controls.logoArea.controls
                            .accessibleImages,
                          index,
                          index + 1
                        )
                      ">
                      <span class="material-symbols-rounded"
                        >keyboard_arrow_down</span
                      >
                    </button>
                  </div>
                  <div class="grid flex-1 grid-cols-3 gap-4">
                    <div>
                      <span
                        class="text-standard-text-secondary mb-1 block text-xs font-semibold"
                        >{{ cmsTextProvider.footer.imageSource }}</span
                      >
                      <aw-image-select
                        [id]="'logoImage' + index"
                        [persistUsageOnSelect]="true"
                        usageLabel="'footer.logoImage'"
                        [formControl]="logoAreaForm.controls.image"
                        [label]="''" />
                    </div>
                    <div>
                      <span
                        class="text-standard-text-secondary mb-1 block text-xs font-semibold"
                        >{{ cmsTextProvider.footer.description }}</span
                      >
                      <aw-translation-accessibility-text-input
                        [entityType]="EntityType.footerLogoArea"
                        [attr.scroll-mark]="
                          logoAreaForm.value.imageAlts?.[0]?.id
                        "
                        [relationType]="TextRelationType.imageAlt"
                        [id]="'logoText' + index"
                        [formControl]="logoAreaForm.controls.imageAlts"
                        [label]="''" />
                    </div>
                    <div>
                      <span
                        class="text-standard-text-secondary mb-1 block text-xs font-semibold"
                        >{{ cmsTextProvider.footer.linkAddressLabel }}</span
                      >
                      <aw-text-input
                        [id]="'logoAddress' + index"
                        [formControl]="logoAreaForm.controls.url"
                        [label]="''" />
                    </div>
                  </div>
                  <aw-button
                    [id]="`delete-logo-${index}`"
                    [type]="'button'"
                    (click)="deleteLogo(index)"
                    [style]="'error'"
                    class="flex h-10 w-10 items-center justify-center p-0">
                    <span class="material-symbols-rounded">delete</span>
                  </aw-button>
                </div>
              }
              <div class="mt-2 flex justify-start">
                <aw-button
                  [id]="'add-logo'"
                  [type]="'button'"
                  (click)="addLogo()"
                  [style]="'primary-alt'"
                  class="w-fit"
                  [text]="cmsTextProvider.shared.add">
                  <span class="material-symbols-rounded mr-1">add</span>
                </aw-button>
              </div>
            </div>
          </div>
        }
      </div>
    </aw-section>

    <!-- AdressArea -->
    <aw-section
      formGroupName="addressArea"
      testIdAffix="addressArea"
      [isCollapsible]="true"
      [isInitiallyCollapsed]="true">
      <aw-section-header>
        <aw-section-headline>
          {{ cmsTextProvider.footer.block }} 2
          <span class="material-symbols-rounded ml-1 align-middle">
            {{
              getIconForBlockType(
                footerForm.controls.addressArea.controls.type.value
              )
            }}
          </span>
        </aw-section-headline>
        <aw-section-header-actions>
          @if (
            !footerForm.controls.layout.controls.hiddenBlocks.value.includes(
              'addressArea'
            )
          ) {
            <span
              class="bg-info-light text-info-main border-info-light rounded-full border px-2.5 py-1 text-xs font-semibold">
              {{ cmsTextProvider.footer.visible }}
            </span>
          } @else {
            <span
              class="bg-standard-surface border-standard-outline-light text-standard-text-secondary rounded-full border px-2.5 py-1 text-xs font-semibold">
              {{ cmsTextProvider.footer.hidden }}
            </span>
          }
        </aw-section-header-actions>
      </aw-section-header>

      <div class="flex flex-col gap-6 py-4">
        <!-- Titel -->
        <div class="flex items-center gap-6">
          <div class="text-standard-text-secondary w-24 text-sm font-semibold">
            {{ cmsTextProvider.shared.title }}
          </div>
          <div class="flex items-center gap-6">
            <div class="w-80">
              <aw-translation-text-input
                [entityType]="EntityType.footerAddressArea"
                [attr.scroll-mark]="
                  footerForm.value.addressArea?.headlines?.[0]?.id
                "
                [relationType]="TextRelationType.headline"
                formControlName="headlines"
                [label]="''"
                id="textTitle">
              </aw-translation-text-input>
            </div>
            <div class="flex items-center gap-2">
              <aw-checkbox
                formControlName="isActive"
                id="textToggle"
                [label]="cmsTextProvider.shared.show"></aw-checkbox>
            </div>
          </div>
        </div>

        <!-- Freitext -->
        <div class="flex items-start gap-6">
          <div
            class="text-standard-text-secondary w-24 pt-3 text-sm font-semibold">
            {{ cmsTextProvider.footer.freeText }}
          </div>
          <div class="flex-1">
            <aw-translation-text-area
              [entityType]="EntityType.footerAddressArea"
              [attr.scroll-mark]="footerForm.value.addressArea?.texts?.[0]?.id"
              [relationType]="TextRelationType.text"
              formControlName="texts"
              [label]="''"
              id="textAreaInput">
            </aw-translation-text-area>
          </div>
        </div>
      </div>
    </aw-section>

    <!-- PaymentArea -->
    <aw-section
      formGroupName="paymentArea"
      testIdAffix="paymentArea"
      [isCollapsible]="true"
      [isInitiallyCollapsed]="true">
      <aw-section-header>
        <aw-section-headline>
          {{ cmsTextProvider.footer.block }} 3
          <span class="material-symbols-rounded ml-1 align-middle">
            {{
              getIconForBlockType(
                footerForm.controls.paymentArea.controls.type.value
              )
            }}
          </span>
        </aw-section-headline>
        <aw-section-header-actions>
          @if (
            !footerForm.controls.layout.controls.hiddenBlocks.value.includes(
              'paymentArea'
            )
          ) {
            <span
              class="bg-info-light text-info-main border-info-light rounded-full border px-2.5 py-1 text-xs font-semibold">
              {{ cmsTextProvider.footer.visible }}
            </span>
          } @else {
            <span
              class="bg-standard-surface border-standard-outline-light text-standard-text-secondary rounded-full border px-2.5 py-1 text-xs font-semibold">
              {{ cmsTextProvider.footer.hidden }}
            </span>
          }
        </aw-section-header-actions>
      </aw-section-header>

      <div class="flex flex-col gap-6 py-4">
        <!-- Titel -->
        <div class="flex items-center gap-6">
          <div class="text-standard-text-secondary w-24 text-sm font-semibold">
            {{ cmsTextProvider.shared.title }}
          </div>
          <div class="flex items-center gap-6">
            <div class="w-80">
              <aw-translation-text-input
                [entityType]="EntityType.footerPaymentArea"
                [attr.scroll-mark]="
                  footerForm.value.paymentArea?.headlines?.[0]?.id
                "
                [relationType]="TextRelationType.headline"
                formControlName="headlines"
                [label]="''"
                id="paymentTitle">
              </aw-translation-text-input>
            </div>
            <div class="flex items-center gap-2">
              <aw-checkbox
                formControlName="isActive"
                id="paymentToggle"
                [label]="cmsTextProvider.shared.show"></aw-checkbox>
            </div>
          </div>
        </div>

        <!-- Payment items list -->
        <div class="flex gap-6">
          <div
            class="text-standard-text-secondary w-24 pt-3 text-sm font-semibold">
            {{ cmsTextProvider.footer.paymentMethods }}
          </div>
          <div class="flex flex-1 flex-col gap-4">
            @for (
              paymentAreaForm of footerForm.controls.paymentArea.controls
                .accessibleImages.controls;
              track paymentAreaForm;
              let index = $index
            ) {
              <div class="flex items-center gap-4">
                <div class="flex flex-col">
                  <button
                    type="button"
                    class="disabled:text-standard-text-disabled text-info-main cursor-pointer hover:text-gray-600 disabled:cursor-not-allowed"
                    [disabled]="index === 0"
                    (click)="
                      moveItemAtIndex(
                        footerForm.controls.paymentArea.controls
                          .accessibleImages,
                        index,
                        index - 1
                      )
                    ">
                    <span class="material-symbols-rounded"
                      >keyboard_arrow_up</span
                    >
                  </button>
                  <button
                    type="button"
                    class="disabled:text-standard-text-disabled text-info-main cursor-pointer hover:text-gray-600 disabled:cursor-not-allowed"
                    [disabled]="
                      index ===
                      footerForm.controls.paymentArea.controls.accessibleImages
                        .length -
                        1
                    "
                    (click)="
                      moveItemAtIndex(
                        footerForm.controls.paymentArea.controls
                          .accessibleImages,
                        index,
                        index + 1
                      )
                    ">
                    <span class="material-symbols-rounded"
                      >keyboard_arrow_down</span
                    >
                  </button>
                </div>
                <div class="grid flex-1 grid-cols-3 gap-4">
                  <aw-payment-select
                    [id]="'paymentImage' + index"
                    [formControl]="paymentAreaForm.controls.image"
                    [label]="cmsTextProvider.footer.paymentProvider" />
                  <aw-translation-accessibility-text-input
                    [entityType]="EntityType.footerPaymentArea"
                    [attr.scroll-mark]="
                      paymentAreaForm.value.imageAlts?.[0]?.id
                    "
                    [relationType]="TextRelationType.imageAlt"
                    [id]="'paymentAlt' + index"
                    [formControl]="paymentAreaForm.controls.imageAlts"
                    [label]="cmsTextProvider.footer.description" />
                  <aw-text-input
                    [id]="'paymentLink' + index"
                    [formControl]="paymentAreaForm.controls.url"
                    [label]="cmsTextProvider.footer.linkAddressLabel" />
                </div>

                <div class="flex items-center gap-1">
                  <button
                    type="button"
                    [id]="'hide-payment-' + index"
                    [attr.data-testid]="'hide-payment-' + index"
                    (click)="hidePayment(index)"
                    class="border-standard-outline-light text-standard-text-secondary hover:bg-standard-hover-main flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border p-0">
                    <span class="material-symbols-rounded">visibility_off</span>
                  </button>
                </div>
              </div>
            }

            @if (hiddenPaymentAreaImages.controls.length > 0) {
              <div
                class="text-standard-text-secondary pt-2 text-sm font-semibold">
                {{ cmsTextProvider.footer.hidden }}
              </div>
              @for (
                hiddenForm of hiddenPaymentAreaImages.controls;
                track hiddenForm;
                let index = $index
              ) {
                <div class="flex items-center gap-4">
                  <div class="flex flex-col">
                    <button
                      type="button"
                      class="disabled:text-standard-text-disabled text-info-main cursor-pointer hover:text-gray-600 disabled:cursor-not-allowed"
                      [disabled]="index === 0"
                      (click)="
                        moveItemAtIndex(
                          hiddenPaymentAreaImages,
                          index,
                          index - 1
                        )
                      ">
                      <span class="material-symbols-rounded"
                        >keyboard_arrow_up</span
                      >
                    </button>
                    <button
                      type="button"
                      class="disabled:text-standard-text-disabled text-info-main cursor-pointer hover:text-gray-600 disabled:cursor-not-allowed"
                      [disabled]="index === hiddenPaymentAreaImages.length - 1"
                      (click)="
                        moveItemAtIndex(
                          hiddenPaymentAreaImages,
                          index,
                          index + 1
                        )
                      ">
                      <span class="material-symbols-rounded"
                        >keyboard_arrow_down</span
                      >
                    </button>
                  </div>
                  <div class="grid flex-1 grid-cols-3 gap-4">
                    <aw-payment-select
                      [id]="'hiddenPaymentImage' + index"
                      [formControl]="hiddenForm.controls.image"
                      [label]="cmsTextProvider.footer.paymentProvider" />
                    <aw-translation-accessibility-text-input
                      [entityType]="EntityType.footerPaymentArea"
                      [attr.scroll-mark]="hiddenForm.value.imageAlts?.[0]?.id"
                      [relationType]="TextRelationType.imageAlt"
                      [id]="'hiddenPaymentAlt' + index"
                      [formControl]="hiddenForm.controls.imageAlts"
                      [label]="cmsTextProvider.footer.description" />
                    <aw-text-input
                      [id]="'hiddenPaymentLink' + index"
                      [formControl]="hiddenForm.controls.url"
                      [label]="cmsTextProvider.footer.linkAddressLabel" />
                  </div>

                  <div class="flex items-center gap-1">
                    <button
                      type="button"
                      [id]="'show-payment-' + index"
                      [attr.data-testid]="'show-payment-' + index"
                      (click)="showPayment(index)"
                      class="border-standard-outline-light text-standard-text-secondary hover:bg-standard-hover-main flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border p-0">
                      <span class="material-symbols-rounded">visibility</span>
                    </button>
                  </div>
                </div>
              }
            }
          </div>
        </div>
      </div>
    </aw-section>

    <!-- LinkArea -->
    <aw-section
      formGroupName="linkArea"
      testIdAffix="linkArea"
      [isCollapsible]="true"
      [isInitiallyCollapsed]="true">
      <aw-section-header>
        <aw-section-headline>
          {{ cmsTextProvider.footer.legalArea }}
          <span class="material-symbols-rounded ml-1 align-middle">
            {{
              getIconForBlockType(
                footerForm.controls.linkArea.controls.type.value
              )
            }}
          </span>
        </aw-section-headline>
        <aw-section-header-actions>
          <span
            class="bg-info2-light text-info2-dark rounded-full px-2.5 py-1 text-xs font-semibold">
            {{ cmsTextProvider.footer.alwaysVisible }}
          </span>
        </aw-section-header-actions>
      </aw-section-header>

      <div class="flex flex-col gap-6 py-4">
        <!-- Links List -->
        <div class="flex gap-6">
          <div
            class="text-standard-text-secondary w-24 pt-3 text-sm font-semibold">
            {{ cmsTextProvider.footer.links }}
          </div>
          <div class="flex w-full flex-col gap-4">
            @for (
              linkAreaForm of footerForm.controls.linkArea.controls.links
                .controls;
              let index = $index;
              track linkAreaForm
            ) {
              <div class="flex items-end gap-4">
                <div class="flex flex-col">
                  <button
                    type="button"
                    class="disabled:text-standard-text-disabled text-info-main cursor-pointer hover:text-gray-600 disabled:cursor-not-allowed"
                    [disabled]="index === 0"
                    (click)="
                      moveItemAtIndex(
                        footerForm.controls.linkArea.controls.links,
                        index,
                        index - 1
                      )
                    ">
                    <span class="material-symbols-rounded"
                      >keyboard_arrow_up</span
                    >
                  </button>
                  <button
                    type="button"
                    class="disabled:text-standard-text-disabled text-info-main cursor-pointer hover:text-gray-600 disabled:cursor-not-allowed"
                    [disabled]="
                      index ===
                      footerForm.controls.linkArea.controls.links.length - 1
                    "
                    (click)="
                      moveItemAtIndex(
                        footerForm.controls.linkArea.controls.links,
                        index,
                        index + 1
                      )
                    ">
                    <span class="material-symbols-rounded"
                      >keyboard_arrow_down</span
                    >
                  </button>
                </div>
                <div class="grid flex-1 grid-cols-2 gap-4">
                  <aw-translation-text-input
                    [entityType]="EntityType.footerLinkArea"
                    [attr.scroll-mark]="linkAreaForm.value.texts?.[0]?.id"
                    [relationType]="TextRelationType.text"
                    [id]="'linkText' + index"
                    [formControl]="linkAreaForm.controls.texts"
                    [label]="cmsTextProvider.footer.description">
                  </aw-translation-text-input>
                  <aw-translation-text-input
                    [entityType]="EntityType.footerLinkArea"
                    [attr.scroll-mark]="linkAreaForm.value.urls?.[0]?.id"
                    [relationType]="TextRelationType.url"
                    [id]="'linkAddress' + index"
                    [formControl]="linkAreaForm.controls.urls"
                    [label]="cmsTextProvider.footer.linkAddressLabel">
                  </aw-translation-text-input>
                </div>

                <div class="mb-1 flex items-center gap-1">
                  @if (isMandatoryFooterLink(linkAreaForm)) {
                    <div class="relative">
                      <div
                        class="text-standard-text-secondary bg-standard-text-secondary/15 peer flex h-10 w-10 items-center justify-center rounded-md">
                        <span class="material-symbols-rounded text-sm"
                          >lock</span
                        >
                      </div>
                      <span
                        class="pointer-events-none absolute bottom-0 left-1/2 z-10 w-44 -translate-x-1/2 translate-y-full rounded-md bg-gray-800 px-1 text-xs text-gray-100 opacity-0 transition-opacity peer-hover:opacity-100">
                        {{ cmsTextProvider.footer.linkCannotBeRemoved }}
                      </span>
                    </div>
                  } @else {
                    <aw-button
                      [id]="'delete-link-' + index"
                      [type]="'button'"
                      (click)="deleteLink(index)"
                      [style]="'error'"
                      class="flex h-10 w-10 items-center justify-center p-0">
                      <span class="material-symbols-rounded">delete</span>
                    </aw-button>
                  }
                </div>
              </div>
            }

            <div class="mt-2 flex justify-start">
              <aw-button
                [id]="'add-entry'"
                [type]="'button'"
                [text]="cmsTextProvider.shared.add"
                (click)="addLink($event)"
                [style]="'primary-alt'"
                class="w-fit">
                <span class="material-symbols-rounded mr-1">add</span>
              </aw-button>
            </div>
          </div>
        </div>
      </div>
    </aw-section>
    <!-- Blocks -->
    <div formArrayName="blocks" class="flex flex-col">
      @for (
        block of footerForm.controls.blocks.controls;
        track block;
        let index = $index
      ) {
        <aw-section
          [testIdAffix]="'block-' + index"
          [formGroupName]="index"
          [isCollapsible]="true"
          [isInitiallyCollapsed]="false">
          <aw-section-header>
            <aw-section-headline>
              {{ cmsTextProvider.footer.block }} {{ getBlockLetter(index) }}
              <span class="material-symbols-rounded ml-1 align-middle">{{
                getIconForBlockType(block.controls.type.value)
              }}</span>
            </aw-section-headline>
            <aw-section-header-actions>
              <div class="flex items-center gap-2">
                @if (
                  footerForm.controls.layout.controls.hiddenBlocks.value.includes(
                    block.controls.id.value ?? ''
                  )
                ) {
                  <span
                    class="bg-standard-surface border-standard-outline-light text-standard-text-secondary h-fit rounded-full border px-2.5 py-1 text-xs font-semibold">
                    {{ cmsTextProvider.footer.hidden }}
                  </span>
                } @else {
                  <span
                    class="bg-info-light text-info-main border-info-light h-fit rounded-full border px-2.5 py-1 text-xs font-semibold">
                    {{ cmsTextProvider.footer.visible }}
                  </span>
                }
              </div>
            </aw-section-header-actions>
          </aw-section-header>
          <div class="flex flex-col gap-6 py-4">
            <div class="flex items-center gap-6">
              <div
                class="text-standard-text-secondary w-24 text-sm font-semibold">
                {{ cmsTextProvider.footer.type }}
              </div>
              <div class="w-80">
                <aw-drop-down
                  [id]="'footer-block-type-' + index"
                  formControlName="type"
                  name="name"
                  valueAttribute="value"
                  [options]="footerBlockTypeOptions"></aw-drop-down>
              </div>
            </div>
            <div class="flex items-center gap-6">
              <div
                class="text-standard-text-secondary w-24 text-sm font-semibold">
                {{ cmsTextProvider.shared.title }}
              </div>
              <div class="flex items-center gap-6">
                <div class="w-80">
                  <aw-translation-text-input
                    [entityType]="EntityType.footerLinkArea"
                    [relationType]="TextRelationType.headline"
                    [id]="'block-title-' + index"
                    formControlName="headlines"
                    [label]="''"></aw-translation-text-input>
                </div>
                <aw-checkbox
                  formControlName="isActive"
                  [label]="cmsTextProvider.shared.show"
                  [id]="'block-active-' + index"></aw-checkbox>
              </div>
            </div>
            @let blockType =
              (block.controls.type.valueChanges | async) ??
              block.controls.type.value;
            @switch (blockType) {
              @case (FooterBlockType.Text) {
                <div class="flex items-start gap-6">
                  <div
                    class="text-standard-text-secondary w-24 pt-3 text-sm font-semibold">
                    {{ cmsTextProvider.footer.freeText }}
                  </div>
                  <div class="flex-1">
                    <aw-translation-text-area
                      [entityType]="EntityType.footerAddressArea"
                      [relationType]="TextRelationType.text"
                      [id]="'block-text-' + index"
                      formControlName="texts"
                      [label]="''"></aw-translation-text-area>
                  </div>
                </div>
              }
              @case (FooterBlockType.Links) {
                @for (
                  link of block.controls.links.controls;
                  track link;
                  let linkIndex = $index
                ) {
                  <div class="grid grid-cols-2 gap-4">
                    <aw-translation-text-input
                      [entityType]="EntityType.footerLinkArea"
                      [relationType]="TextRelationType.text"
                      [id]="'block-link-text-' + index + '-' + linkIndex"
                      [formControl]="link.controls.texts"
                      [label]="
                        cmsTextProvider.footer.description
                      "></aw-translation-text-input>
                    <aw-translation-text-input
                      [entityType]="EntityType.footerLinkArea"
                      [relationType]="TextRelationType.url"
                      [id]="'block-link-url-' + index + '-' + linkIndex"
                      [formControl]="link.controls.urls"
                      [label]="
                        cmsTextProvider.footer.linkAddressLabel
                      "></aw-translation-text-input>
                  </div>
                }
                <aw-button
                  [id]="'add-block-link-' + index"
                  type="button"
                  [style]="'primary-alt'"
                  (click)="addBlockLink(block)"
                  class="w-fit"
                  [text]="cmsTextProvider.shared.add">
                  <span class="material-symbols-rounded mr-1"
                    >add</span
                  ></aw-button
                >
              }
              @case (FooterBlockType.Logo) {
                <div class="flex items-start gap-6">
                  <div
                    class="text-standard-text-secondary w-24 pt-3 text-sm font-semibold">
                    {{ cmsTextProvider.footer.logo }}
                  </div>
                  <div class="flex-1">
                    @let firstImage = getFirstImageControl(block);
                    @if (firstImage) {
                      <div class="grid grid-cols-3 gap-4">
                        <div>
                          <span
                            class="text-standard-text-secondary mb-1 block text-xs font-semibold"
                            >{{ cmsTextProvider.footer.imageSource }}</span
                          >
                          <aw-image-select
                            [id]="'block-logo-image-' + index"
                            [formControl]="firstImage.controls.image"
                            [label]="''"></aw-image-select>
                        </div>
                        <div>
                          <span
                            class="text-standard-text-secondary mb-1 block text-xs font-semibold"
                            >{{ cmsTextProvider.footer.description }}</span
                          >
                          <aw-translation-accessibility-text-input
                            [entityType]="EntityType.footerPaymentArea"
                            [relationType]="TextRelationType.imageAlt"
                            [id]="'block-logo-alt-' + index"
                            [formControl]="firstImage.controls.imageAlts"
                            [label]="''"></aw-translation-accessibility-text-input>
                        </div>
                        <div>
                          <span
                            class="text-standard-text-secondary mb-1 block text-xs font-semibold"
                            >{{ cmsTextProvider.footer.linkAddressLabel }}</span
                          >
                          <aw-text-input
                            [id]="'block-logo-url-' + index"
                            [formControl]="firstImage.controls.url"
                            [label]="''"></aw-text-input>
                        </div>
                      </div>
                    }
                  </div>
                </div>
              }
              @case (FooterBlockType.Images) {
                <div class="flex items-start gap-6">
                  <div
                    class="text-standard-text-secondary w-24 pt-3 text-sm font-semibold">
                    {{ cmsTextProvider.footer.links }}
                  </div>
                  <div class="flex flex-1 flex-col gap-4">
                    <div class="w-60">
                      <aw-button-group
                        [id]="'block-orientation-' + index"
                        [compact]="true"
                        [options]="orientationOptions"
                        [formControl]="
                          block.controls.orientation
                        "></aw-button-group>
                    </div>
                    @for (
                      image of block.controls.accessibleImages.controls;
                      track image;
                      let imageIndex = $index
                    ) {
                      <div class="flex items-end gap-4">
                        <div class="mb-1 flex flex-col">
                          <button
                            type="button"
                            class="disabled:text-standard-text-disabled text-info-main cursor-pointer hover:text-gray-600 disabled:cursor-not-allowed"
                            [disabled]="imageIndex === 0"
                            (click)="
                              moveItemAtIndex(
                                block.controls.accessibleImages,
                                imageIndex,
                                imageIndex - 1
                              )
                            ">
                            <span class="material-symbols-rounded"
                              >keyboard_arrow_up</span
                            >
                          </button>
                          <button
                            type="button"
                            class="disabled:text-standard-text-disabled text-info-main cursor-pointer hover:text-gray-600 disabled:cursor-not-allowed"
                            [disabled]="
                              imageIndex ===
                              block.controls.accessibleImages.length - 1
                            "
                            (click)="
                              moveItemAtIndex(
                                block.controls.accessibleImages,
                                imageIndex,
                                imageIndex + 1
                              )
                            ">
                            <span class="material-symbols-rounded"
                              >keyboard_arrow_down</span
                            >
                          </button>
                        </div>
                        <div class="grid flex-1 grid-cols-3 gap-4">
                          <div>
                            <span
                              class="text-standard-text-secondary mb-1 block text-xs font-semibold"
                              >{{ cmsTextProvider.footer.imageSource }}</span
                            >
                            <aw-image-select
                              [id]="'block-image-' + index + '-' + imageIndex"
                              [formControl]="image.controls.image"
                              [label]="''"></aw-image-select>
                          </div>
                          <div>
                            <span
                              class="text-standard-text-secondary mb-1 block text-xs font-semibold"
                              >{{ cmsTextProvider.footer.description }}</span
                            >
                            <aw-translation-accessibility-text-input
                              [entityType]="EntityType.footerPaymentArea"
                              [relationType]="TextRelationType.imageAlt"
                              [id]="
                                'block-image-alt-' + index + '-' + imageIndex
                              "
                              [formControl]="image.controls.imageAlts"
                              [label]="''"></aw-translation-accessibility-text-input>
                          </div>
                          <div>
                            <span
                              class="text-standard-text-secondary mb-1 block text-xs font-semibold"
                              >{{
                                cmsTextProvider.footer.linkAddressLabel
                              }}</span
                            >
                            <aw-text-input
                              [id]="
                                'block-image-url-' + index + '-' + imageIndex
                              "
                              [formControl]="image.controls.url"
                              [label]="''"></aw-text-input>
                          </div>
                        </div>
                        <aw-button
                          [id]="
                            'delete-block-image-' + index + '-' + imageIndex
                          "
                          type="button"
                          (click)="removeBlockImage(block, imageIndex)"
                          [style]="'error'"
                          class="flex h-10 w-10 items-center justify-center p-0">
                          <span class="material-symbols-rounded">delete</span>
                        </aw-button>
                      </div>
                    }
                    <aw-button
                      [id]="'add-block-image-' + index"
                      type="button"
                      [style]="'primary-alt'"
                      (click)="addBlockImage(block)"
                      class="w-fit"
                      [text]="cmsTextProvider.shared.add">
                      <span class="material-symbols-rounded mr-1">add</span>
                    </aw-button>
                  </div>
                </div>
              }
              @default {
                <div class="flex justify-end">
                  <aw-button
                    [id]="'delete-block-' + index"
                    [type]="'button'"
                    (click)="removeBlock(index)"
                    [style]="'error'"
                    class="flex w-fit items-center justify-center p-0">
                    {{ cmsTextProvider.shared.delete }}
                  </aw-button>
                </div>
              }
            }
          </div>
        </aw-section>
      }
    </div>
    <div class="mt-2 flex justify-start px-6 py-4">
      <aw-button
        id="add-footer-block"
        type="button"
        [style]="'primary-alt'"
        [isDisabled]="
          footerForm.controls.blocks.length >= maximumCustomFooterBlocks
        "
        (click)="addBlock()"
        class="w-fit"
        [text]="cmsTextProvider.shared.add"
        ><span class="material-symbols-rounded mr-1">add</span></aw-button
      >
    </div>
  </aw-section>
  <aw-preview [components]="[FooterComponent]" [previewData]="previewData()" />
}
